import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  Area, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { FiChevronLeft, FiChevronRight, FiInfo } from "react-icons/fi";
import { Sparkline } from "../../activity-logs/components/primitives";
import {
  Section, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { Notice, Tag } from "../../creditos/styles";
import { RoundButton } from "../../creditos/components/ControlBar";
import { ChartTooltip } from "../../creditos/components/charts";
import { formatCompact, formatCurrency, formatPercent } from "../../creditos/dashboardData";
import { hairline, surface } from "../../../styles/designTokens";
import { PlotHead, PlotRow, directionColor, signed } from "../dashboard/ReformaDashboard";
import { buildReformaDashboard } from "../dashboard/reformaData";
import { useReformaParams } from "../hooks/useReformaParams";
import { AnimatedMoney as MoneyValue, Num } from "../components/Motion";

/**
 * Aba Transição: quando cada tributo sai, quando CBS e IBS entram e quando o caixa
 * sente. Elementos próprios: cruzamento dos regimes e partitura do cronograma, em que
 * cada tributo é uma escada de alíquotas com o marco legal alinhado abaixo. Um único
 * ano selecionado sincroniza o gráfico, a partitura, o painel e as tabelas.
 */

const THIS_YEAR = new Date().getFullYear();
const LEAVING = IOS.blue;
const CBS = IOS.purple;
const IBS = IOS.green;
const label2 = ({ theme }) => tokens.label2(theme);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const spark = { width: 80, height: 24, points: 60 };
const rate = (v) => `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

const Legend = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 0.76em;
  .ref { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; color: ${label2}; }
  .ref i { width: 12px; border-top: 1.5px dashed currentColor; }
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  .y { font-size: 1.72em; font-weight: 600; letter-spacing: -0.04em; line-height: 1.05; }
  .f { margin-top: 3px; font-size: 0.78em; color: ${label2}; }
  .b { display: flex; gap: 6px; }
`;

const Facts = styled.dl`
  margin: auto 0 0;
  display: grid;
  grid-template-columns: 1fr auto;
  font-size: 0.82em;
  dt, dd { margin: 0; padding: 8px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  dt { color: ${label2}; }
  dd { font-weight: 600; letter-spacing: -0.02em; text-align: right; white-space: nowrap; }
`;

/* A partitura: rótulo + 8 colunas, com trilho, escadas e marcos nas mesmas colunas */
const SCORE_COLUMNS = "108px repeat(8, minmax(0, 1fr))";

const ScoreScroll = styled.div`
  margin-top: 16px;
  overflow-x: auto;
  scrollbar-width: thin;
  > div { min-width: 1020px; }
`;

const Rail = styled.div`
  display: grid;
  grid-template-columns: ${SCORE_COLUMNS};
  position: relative;
  padding-bottom: 12px;

  /* A linha do tempo liga os pontos, do primeiro ao último ano */
  &::before {
    content: "";
    position: absolute;
    left: 108px;
    right: calc((100% - 108px) / 8 - 8px);
    bottom: 15px;
    height: 1px;
    background: ${({ theme }) => hairline(theme, true)};
  }

  button {
    position: relative;
    padding: 0 0 18px;
    border: none;
    background: none;
    color: ${label2};
    font: inherit;
    font-size: 0.86em;
    font-weight: 500;
    letter-spacing: -0.01em;
    text-align: left;
    cursor: pointer;
    font-variant-numeric: tabular-nums;
    &.on { color: ${({ theme }) => theme.text}; font-weight: 600; }
    &:focus-visible { outline: 2px solid ${({ theme }) => tokens.label3(theme)}; outline-offset: 2px; border-radius: 6px; }
    small { margin-left: 5px; font-size: 0.84em; font-weight: 400; color: ${({ theme }) => tokens.label3(theme)}; }
    i { position: absolute; left: 0; bottom: 0; width: 7px; height: 7px; border-radius: 50%; background: ${({ theme }) => tokens.label3(theme)}; box-shadow: 0 0 0 3px ${({ theme }) => tokens.solid(theme)}; }
    &.on i { background: ${({ theme }) => theme.text}; }
    &.now i { background: ${IOS.green}; }
  }
`;

const StepRow = styled.div`
  display: grid;
  grid-template-columns: ${SCORE_COLUMNS};
  align-items: stretch;
  border-top: 1px solid ${({ theme }) => hairline(theme)};

  .name { display: flex; flex-direction: column; justify-content: center; font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; }
  .name small { margin-top: 1px; font-size: 0.84em; font-weight: 400; letter-spacing: 0; color: ${label2}; }
`;

// Degrau: a altura acompanha a alíquota, e a linha do topo desenha a escada da linha inteira
const Step = styled.button`
  position: relative;
  height: 52px;
  padding: 0;
  border: none;
  background: ${({ $on, theme }) => ($on ? surface(theme, 1) : "transparent")};
  cursor: pointer;
  transition: background 160ms ease;
  &:hover { background: ${({ theme }) => surface(theme, 1)}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => tokens.label3(theme)}; outline-offset: -2px; border-radius: 6px; }

  .lvl { position: absolute; left: 0; right: 0; bottom: 0; }
  .val {
    position: absolute;
    left: 8px;
    top: 7px;
    z-index: 1;
    font-family: inherit;
    font-size: 0.78em;
    font-weight: ${({ $on }) => ($on ? 600 : 500)};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: ${({ theme, $zero }) => ($zero ? tokens.label3(theme) : theme.text)};
  }
`;

const Milestones = styled.div`
  display: grid;
  grid-template-columns: ${SCORE_COLUMNS};
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};

  .name { font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; }
  .m { padding-right: 16px; }
  .t { font-size: 0.8em; font-weight: 500; letter-spacing: -0.01em; line-height: 1.3; }
  .d { margin-top: 5px; font-size: 0.72em; font-weight: 300; line-height: 1.45; color: ${label2}; }
  .m.off .t { color: ${label2}; }
  .tag { margin-top: 7px; font-size: 0.72em; }
`;

const MixHead = styled.div`
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr) 84px 84px 84px;
  gap: 12px;
  margin-top: 16px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  > :nth-child(n + 3) { text-align: right; }
`;

const MixRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr) 84px 84px 84px;
  gap: 12px;
  align-items: center;
  min-height: 50px;
  padding: 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ $on, theme }) => ($on ? surface(theme, 1) : "transparent")};
  color: ${({ theme }) => theme.text};
  font: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  transition: background 160ms ease;
  &:hover { background: ${({ theme }) => surface(theme, 1)}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => tokens.label3(theme)}; outline-offset: -2px; border-radius: 6px; }

  .yr { font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .yr small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 400; letter-spacing: 0; color: ${label2}; }
  .mix { display: flex; align-items: center; gap: 8px; min-width: 0; }
  .track { flex: 1; display: flex; gap: 1px; height: 5px; border-radius: 3px; overflow: hidden; }
  .track span { display: block; min-width: 0; }
  .pct { width: 30px; text-align: right; font-size: 0.74em; color: ${label2}; }
  .n { text-align: right; white-space: nowrap; font-size: 0.82em; color: ${label2}; }
  .tot { text-align: right; white-space: nowrap; font-size: 0.94em; font-weight: 600; letter-spacing: -0.025em; }
`;

function Crossing({ data, selected, onSelect }) {
  const theme = useTheme();
  const { years, turningYear } = data;
  const axis = { tick: { fontSize: 10, fill: tokens.label2(theme) }, axisLine: false, tickLine: false };
  return (
    <Widget $span={3} $delay={150}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
        <div>
          <div className="w-title">Cruzamento dos regimes</div>
          <div className="w-sub">
            {turningYear
              ? <>Virada: <strong style={{ color: tokens.label(theme) }}>{turningYear}</strong> · CBS e IBS passam o regime vigente. Clique em um ano</>
              : "CBS e IBS não passam o regime vigente neste cenário. Clique em um ano"}
          </div>
        </div>
        <Legend>
          <Tag $color={LEAVING}>Vigente</Tag>
          <Tag $color={IBS}>CBS + IBS</Tag>
          <span className="ref"><i /> Total</span>
        </Legend>
      </div>
      <ResponsiveContainer width="100%" height={268}>
        <ComposedChart
          data={years}
          margin={{ top: 18, right: 8, left: 0, bottom: 0 }}
          onClick={(e) => { if (e?.activeLabel) onSelect(Number(e.activeLabel)); }}
          style={{ cursor: "pointer" }}
        >
          <defs>
            <linearGradient id="tr-vigente" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={LEAVING} stopOpacity={0.22} />
              <stop offset="100%" stopColor={LEAVING} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="tr-futuro" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={IBS} stopOpacity={0.22} />
              <stop offset="100%" stopColor={IBS} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />
          <XAxis dataKey="label" tickFormatter={(l) => (Number(l) === THIS_YEAR ? `${l} · hoje` : l)} {...axis} />
          <YAxis width={70} tickFormatter={formatCompact} {...axis} />
          <Tooltip content={<ChartTooltip showTotal={false} />} cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }} />
          <ReferenceLine x={String(selected)} stroke={tokens.label3(theme)} />
          {turningYear && (
            <ReferenceLine
              x={String(turningYear)}
              stroke={tokens.label2(theme)}
              strokeDasharray="3 3"
              label={{ value: "Virada", position: "top", fontSize: 10, fontWeight: 600, fill: tokens.label(theme) }}
            />
          )}
          <Area type="monotone" dataKey="vigente" name="Vigente" stroke={LEAVING} strokeWidth={1.5} fill="url(#tr-vigente)" isAnimationActive={false} />
          <Area type="monotone" dataKey="futuro" name="CBS + IBS" stroke={IBS} strokeWidth={1.5} fill="url(#tr-futuro)" isAnimationActive={false} />
          <Line type="monotone" dataKey="total" name="Total" stroke={tokens.label2(theme)} strokeWidth={1.25} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Widget>
  );
}

function YearPanel({ year, years, onSelect, previous }) {
  const theme = useTheme();
  const index = years.findIndex((y) => y.ano === year.ano);
  const share = year.total ? (year.futuro / year.total) * 100 : 0;
  const slices = [
    { key: "futuro", value: Math.max(0, year.futuro), color: IBS },
    { key: "vigente", value: Math.max(0, year.vigente), color: LEAVING },
  ].filter((s) => s.value > 0);
  return (
    <Widget $delay={200} style={{ display: "flex", flexDirection: "column" }}>
      <YearNav>
        <div>
          <div className="y">{year.ano}</div>
          <div className="f">{year.fase}{year.ano === THIS_YEAR ? " · hoje" : ""}</div>
        </div>
        <div className="b">
          <RoundButton type="button" aria-label="Ano anterior" disabled={index === 0} onClick={() => onSelect(years[index - 1].ano)} style={{ width: 30, height: 30 }}><FiChevronLeft /></RoundButton>
          <RoundButton type="button" aria-label="Próximo ano" disabled={index === years.length - 1} onClick={() => onSelect(years[index + 1].ano)} style={{ width: 30, height: 30 }}><FiChevronRight /></RoundButton>
        </div>
      </YearNav>
      <div style={{ position: "relative", height: 150, margin: "8px 0" }} role="img" aria-label={`${formatPercent(share)} da carga de ${year.ano} em CBS e IBS`}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" innerRadius={56} outerRadius={68} cornerRadius={4} paddingAngle={slices.length > 1 ? 3 : 0} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
              {slices.map((s) => <Cell key={s.key} fill={s.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ fontSize: "1.2em", fontWeight: 600, letterSpacing: "-0.03em" }}>{Math.round(share)}<span style={{ fontSize: "0.6em", opacity: 0.5, marginLeft: 1 }}>%</span></div>
          <div style={{ fontSize: "0.68em", color: tokens.label2(theme) }}>CBS + IBS</div>
        </div>
      </div>
      <Facts>
        <dt>Carga do ano</dt><dd title={formatCurrency(year.total)}><Num value={year.total} format={formatCompact} /></dd>
        {previous && <><dt>Sobre {previous.ano}</dt><dd style={{ color: Math.abs(year.delta) >= 1 ? directionColor(year.delta) : undefined }}>{Math.abs(year.delta) < 1 ? "R$ 0" : signed(year.delta)}</dd></>}
        <dt>Sobre a carga atual</dt><dd style={{ color: Math.abs(year.fromToday) >= 1 ? directionColor(year.fromToday) : undefined }}><Num value={year.fromToday} format={signed} /></dd>
      </Facts>
    </Widget>
  );
}

function Score({ years, selected, onSelect, turningYear }) {
  const rows = [
    { key: "keepsPisCofins", label: "PIS/COFINS", sub: "Parcela mantida", color: LEAVING, top: 100 },
    { key: "keepsIcmsIss", label: "ICMS e ISS", sub: "Parcela mantida", color: LEAVING, top: 100 },
    { key: "rateCbs", label: "CBS", sub: "Alíquota", color: CBS },
    { key: "rateIbs", label: "IBS", sub: "Alíquota", color: IBS },
  ];
  return (
    <Widget $delay={250}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Cronograma legal da transição</div>
          <div className="w-sub">O que sai, o que entra e o marco de cada ano, pela LC 214/2025</div>
        </div>
        <Legend>
          <Tag $color={LEAVING}>Sai</Tag>
          <Tag $color={CBS}>CBS entra</Tag>
          <Tag $color={IBS}>IBS entra</Tag>
        </Legend>
      </div>
      <ScoreScroll>
        <div>
          <Rail>
            <span />
            {years.map((y) => (
              <button key={y.ano} type="button" className={`${y.ano === selected ? "on" : ""} ${y.ano === THIS_YEAR ? "now" : ""}`} aria-pressed={y.ano === selected} onClick={() => onSelect(y.ano)}>
                {y.ano}{y.ano === THIS_YEAR && <small>hoje</small>}
                <i />
              </button>
            ))}
          </Rail>
          {rows.map((row) => {
            const top = row.top || Math.max(1, ...years.map((y) => y[row.key]));
            return (
              <StepRow key={row.key}>
                <div className="name">{row.label}<small>{row.sub}</small></div>
                {years.map((y) => {
                  const value = y[row.key];
                  const height = Math.max(0, Math.min(1, value / top)) * 58;
                  const on = y.ano === selected;
                  return (
                    <Step key={y.ano} type="button" $on={on} $zero={!value} aria-label={`${row.label} em ${y.ano}: ${rate(value)}`} onClick={() => onSelect(y.ano)}>
                      {value > 0 && (
                        <span
                          className="lvl"
                          style={{
                            height: `${height}%`,
                            minHeight: 2,
                            background: `${row.color}${on ? "33" : "1f"}`,
                            borderTop: `1.5px solid ${row.color}`,
                          }}
                        />
                      )}
                      <span className="val"><Num value={value} format={rate} /></span>
                    </Step>
                  );
                })}
              </StepRow>
            );
          })}
          <Milestones>
            <div className="name">Marco legal</div>
            {years.map((y) => (
              <div key={y.ano} className={`m${y.ano === selected ? "" : " off"}`}>
                <div className="t">{y.marcoTitulo}</div>
                <div className="d">{y.marcoTexto}</div>
                {y.ano === turningYear && <div className="tag"><Tag $color={IBS}>Virada neste projeto</Tag></div>}
              </div>
            ))}
          </Milestones>
        </div>
      </ScoreScroll>
    </Widget>
  );
}

function MixTable({ years, selected, onSelect, turningYear }) {
  return (
    <Widget $span={2} $delay={300}>
      <div className="w-title">Evolução ano a ano</div>
      <div className="w-sub">Como o regime futuro toma o lugar do vigente na carga de cada ano</div>
      <MixHead aria-hidden="true">
        <span>Ano</span>
        <span>% CBS + IBS</span>
        <span>Vigente</span>
        <span>Futuro</span>
        <span>Total</span>
      </MixHead>
      {years.map((y) => {
        const share = y.total ? (y.futuro / y.total) * 100 : 0;
        return (
          <MixRow key={y.ano} type="button" $on={y.ano === selected} aria-pressed={y.ano === selected} onClick={() => onSelect(y.ano)}>
            <span className="yr">{y.ano}<small>{y.ano === turningYear ? "Virada" : y.fase}</small></span>
            <span className="mix">
              <span className="track" role="img" aria-label={`${formatPercent(share)} em CBS e IBS`}>
                <span style={{ width: `${Math.max(0, Math.min(100, share))}%`, background: IBS, opacity: 0.8 }} />
                <span style={{ flex: 1, background: LEAVING, opacity: 0.55 }} />
              </span>
              <span className="pct">{Math.round(share)}%</span>
            </span>
            <span className="n" title={formatCurrency(y.vigente)}><Num value={y.vigente} format={formatCompact} /></span>
            <span className="n" title={formatCurrency(y.futuro)}><Num value={y.futuro} format={formatCompact} /></span>
            <span className="tot" title={formatCurrency(y.total)}><Num value={y.total} format={formatCompact} /></span>
          </MixRow>
        );
      })}
    </Widget>
  );
}

function YearSteps({ years }) {
  const max = Math.max(1, ...years.map((y) => Math.abs(y.delta)));
  return (
    <Widget $span={2} $delay={350}>
      <div className="w-title">Quando o caixa sente</div>
      <div className="w-sub">Quanto a carga muda de um ano para o outro</div>
      <PlotHead aria-hidden="true">
        <span>Ano</span>
        <span className="dir"><span>menos imposto</span><span>mais imposto</span></span>
        <span className="end">Variação</span>
      </PlotHead>
      {years.map((y, i) => {
        const ratio = Math.abs(y.delta) / max;
        const color = directionColor(y.delta);
        const up = y.delta >= 0;
        const strength = Math.round((0.28 + ratio * 0.72) * 255).toString(16).padStart(2, "0");
        return (
          <PlotRow key={y.ano}>
            <div className="name">{y.ano}<small>{i === 0 ? "Sobre a carga atual" : `Sobre ${years[i - 1].ano}`}</small></div>
            <div className="plot">
              {Math.abs(y.delta) >= 1 && (
                <i
                  className={up ? "up" : "down"}
                  style={{
                    width: `${ratio * 50}%`,
                    left: up ? "50%" : `${50 - ratio * 50}%`,
                    background: `linear-gradient(to ${up ? "right" : "left"}, ${color}33, ${color}${strength})`,
                  }}
                />
              )}
            </div>
            <div className="num" title={formatCurrency(y.delta)}>{Math.abs(y.delta) < 1 ? "R$ 0" : signed(y.delta)}</div>
          </PlotRow>
        );
      })}
    </Widget>
  );
}

export default function TransitionDashboard({ fiscalData, companyFiltered }) {
  const { params } = useReformaParams();
  const data = useMemo(() => buildReformaDashboard(fiscalData, params), [fiscalData, params]);
  const { years, turningYear } = data;
  const [selected, setSelected] = useState(years.some((y) => y.ano === THIS_YEAR) ? THIS_YEAR : years[0].ano);
  const index = years.findIndex((y) => y.ano === selected);
  const year = years[index];
  const first = years[0];
  const last = years[years.length - 1];
  const isFirst = index === 0;
  const isLast = index === years.length - 1;
  // Até o ano escolhido: sobre 2026 ou, no próprio 2026, sobre a carga atual
  const reference = isFirst ? data.current.total : first.total;
  const change = year.total - reference;
  const changePercent = reference ? (change / reference) * 100 : 0;
  const toLast = last.total - year.total;
  const untilTurning = turningYear ? turningYear - year.ano : null;
  let turningNote = "Sem virada neste cenário";
  if (untilTurning === 0) turningNote = `Acontece em ${year.ano}`;
  else if (untilTurning > 0) turningNote = `Faltam ${untilTurning} ${untilTurning === 1 ? "ano" : "anos"} a partir de ${year.ano}`;
  else if (untilTurning < 0) turningNote = `${year.ano} já passou da virada`;
  let changeLabel = `Sem variação em ${year.ano}`;
  if (Math.abs(change) >= 1) changeLabel = `${change > 0 ? "Aumento" : "Redução"} ${isFirst ? "em" : "até"} ${year.ano}`;
  const upToYear = years.slice(0, index + 1);
  const shared = { selected, onSelect: setSelected };

  return (
    <>
      <Section $first>
        {companyFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Com empresa selecionada, a transição mistura bases: PIS/COFINS é apurado na matriz, e as despesas (M105) são do projeto inteiro.</span>
          </Notice>
        )}
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(LEAVING)} Carga total {year.ano}</div>
            <div className="w-value"><MoneyValue value={year.total} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{year.fase}{year.ano === THIS_YEAR ? " · hoje" : ""}</span>
              {upToYear.length > 1 && <Sparkline data={upToYear.map((y) => y.total)} color={LEAVING} {...spark} />}
            </div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">{dot(IBS)} Carga total 2033</div>
            <div className="w-value"><MoneyValue value={last.total} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{isLast || Math.abs(toLast) < 1 ? "Regime pleno CBS+IBS" : <><span style={{ color: directionColor(toLast) }}><Num value={toLast} format={signed} /></span> a partir de {year.ano}</>}</span>
              {years.slice(index).length > 1 && <Sparkline data={years.slice(index).map((y) => y.total)} color={IBS} {...spark} />}
            </div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">{dot(Math.abs(change) >= 1 ? directionColor(change) : IOS.gray)} {changeLabel}</div>
            <div className="w-value"><MoneyValue value={Math.abs(change)} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span><span style={{ color: Math.abs(change) >= 1 ? directionColor(change) : undefined }}>{change > 0 ? "+" : ""}<Num value={changePercent} format={formatPercent} /></span> sobre {isFirst ? "a carga atual" : "2026"}</span>
              {upToYear.length > 1 && <Sparkline data={upToYear.map((y) => y.total - reference)} color={directionColor(change)} {...spark} />}
            </div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head">{dot(IOS.gray)} Ano de virada</div>
            <div className="w-value">{turningYear || "—"}</div>
            <div className="w-foot">{turningNote}</div>
          </Widget>
        </WidgetGrid>
        <Notice>
          <FiInfo />
          <span>Cronograma da LC 214/2025. A transição do PIS/COFINS/ICMS/ISS para CBS+IBS é gradual entre 2026 e 2033: a carga muda de composição ano a ano, mesmo sem alteração nas regras de negócio.</span>
        </Notice>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Crossing data={data} {...shared} />
          <YearPanel year={years[index]} years={years} onSelect={setSelected} previous={index > 0 ? years[index - 1] : null} />
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <Score years={years} turningYear={turningYear} {...shared} />
      </Section>

      <Section $delay={180}>
        <WidgetGrid>
          <MixTable years={years} turningYear={turningYear} {...shared} />
          <YearSteps years={years} />
        </WidgetGrid>
      </Section>
    </>
  );
}
